import {
  AdditiveBlending,
  Color,
  InstancedBufferAttribute,
  InstancedBufferGeometry,
  Float32BufferAttribute,
  Mesh,
  SpriteNodeMaterial,
} from 'three/webgpu';
import { type AudioFrame } from '../core/audio-frame.js';
import { Gentle } from '../core/gentle.js';
import { BreathingPulse } from '../core/pulse.js';
import { Smoothed } from '../core/smoothed.js';
import { type Bounds, ShapeBake } from './baker.js';
import { Director, type Segment, Timeline } from './director.js';
import { buildMorphGraph, MorphUniforms } from './field-shader.js';
import { SHAPES } from './registry.js';
import { type ShapeDefinition } from './shape.js';

const PARTICLES = 190_000;
/** Particles placed per frame while a form bakes — about a millisecond of work. */
const BAKE_BUDGET = 4000;

/** What a page can change about the field without touching how it works. */
export interface MorphFieldOptions {
  /** The forms this field cycles through. Defaults to the full registry. */
  readonly shapes?: readonly ShapeDefinition[] | undefined;
  /** Multiplies the music-driven radial swell — 1 for the main show. */
  readonly pulse?: number | undefined;
}

interface Slot {
  readonly bake: ShapeBake;
  readonly form: InstancedBufferAttribute;
  readonly motion: InstancedBufferAttribute;
  segment: Segment;
  bounds: Bounds;
  spin: number;
  uploaded: boolean;
}

function quadGeometry(): InstancedBufferGeometry {
  const geometry = new InstancedBufferGeometry();
  const corners = [-0.5, -0.5, 0, 0.5, -0.5, 0, 0.5, 0.5, 0, -0.5, 0.5, 0];
  geometry.setAttribute('position', new Float32BufferAttribute(corners, 3));
  geometry.setAttribute('uv', new Float32BufferAttribute([0, 0, 1, 0, 1, 1, 0, 1], 2));
  geometry.setIndex([0, 1, 2, 0, 2, 3]);
  geometry.instanceCount = PARTICLES;
  return geometry;
}

/** Rotate a hex colour's hue, so a form is never lit the same way twice. */
function tinted(hex: number, hueShift: number): Color {
  const color = new Color(hex);
  const hsl = { h: 0, s: 0, l: 0 };
  color.getHSL(hsl);
  return color.setHSL((hsl.h + hueShift + 1) % 1, Math.min(1, hsl.s * 1.15), hsl.l);
}

/**
 * The visualiser itself: a couple of hundred thousand sprites that are always
 * some recognisable thing, and always on their way to being the next one.
 *
 * Two buffer slots hold two baked forms. One is on screen; the other is quietly
 * being built a few thousand particles per frame. When it is ready the field
 * flows from one to the other, the roles swap, and the slot that was just
 * vacated starts building whatever comes next — so it never stops and never
 * needs to pause for work.
 */
export class MorphField {
  public readonly mesh: Mesh;
  /** The accent colour of whatever is on screen, for the sky to agree with. */
  public readonly ambient = new Color();
  private readonly uniforms = new MorphUniforms();
  private readonly shapes: readonly ShapeDefinition[];
  private pulse: number;
  private readonly timeline: Timeline;
  /** Breathing envelope: rises smoothly on onsets and relaxes buoyantly. */
  private readonly breathingPulse = new BreathingPulse(0.09, 0.48);
  /** The drift oscillators' clock, advanced faster when the music is busy. */
  private driftClock = 0;
  private readonly slots: readonly [Slot, Slot];
  private flip = 0;
  private elapsed = 0;
  private visits = 0;
  private readonly glow = new Gentle(0.55, 0.45, 1);
  private readonly size = new Gentle(0.4, 0.06, 0.05);
  private readonly opacity = new Gentle(0.6, 0.25, 0.34);
  private readonly shimmer = new Gentle(0.7, 0.35, 0.1);
  private readonly flowLevel = new Smoothed(0.35);
  private readonly breath = new Smoothed(0.22);

  constructor(seed: number, options: MorphFieldOptions = {}) {
    this.shapes = options.shapes ?? SHAPES;
    this.pulse = options.pulse ?? 1;
    const director = new Director(this.shapes.length, seed);
    this.timeline = new Timeline(director);
    const geometry = quadGeometry();
    this.slots = [
      this.makeSlot(geometry, 0, this.timeline.current),
      this.makeSlot(geometry, 1, this.timeline.incoming),
    ];

    // The first form is baked up front: there is nothing on screen to protect.
    this.bakeNow(0);
    this.beginBake(1, this.timeline.incoming);
    console.info(
      `DEBUG ${this.shapeAt(this.timeline.current.shape).name} bounds=${JSON.stringify(this.slots[0].bounds)} head=${JSON.stringify([...this.slots[0].bake.form.slice(0, 12)])} written=${String(this.slots[0].bake.done)}`,
    );
    this.applySegment(0, this.timeline.current);
    this.applySegment(1, this.timeline.incoming);
    this.focusOn(this.slots[0].bounds);

    const graph = buildMorphGraph(this.uniforms);
    const material = new SpriteNodeMaterial();
    material.positionNode = graph.position;
    material.colorNode = graph.color;
    material.opacityNode = graph.alpha;
    material.scaleNode = graph.scale;
    material.transparent = true;
    material.depthWrite = false;
    material.blending = AdditiveBlending;
    this.mesh = new Mesh(geometry, material);
    this.mesh.frustumCulled = false;
  }

  /** Where the current composition sits, so the camera can frame it. */
  public get focus(): Bounds {
    return this.slotAt(this.flip).bounds;
  }

  /** How the camera should move while this form is on screen. */
  public get segment(): Segment {
    return this.timeline.current;
  }

  /** True while the field is between forms — the camera eases off during these. */
  public get blending(): number {
    return this.timeline.progress;
  }

  /** How far back the current form wants the camera. */
  public get framing(): number {
    return this.shapeAt(this.timeline.current.shape).framing;
  }

  /** Counts arrivals, so the camera knows when a new shot has begun. */
  public get visit(): number {
    return this.visits;
  }

  /** The name of the thing currently on screen. */
  public get title(): string {
    return this.shapeAt(this.timeline.current.shape).name;
  }

  /** Live pulse dial: how hard the field swells, kicks and flashes with the music. */
  public setPulse(value: number): void {
    this.pulse = Math.max(0, value);
  }

  public update(frame: AudioFrame, deltaSeconds: number): void {
    this.elapsed += deltaSeconds;
    const free = this.flip === 0 ? 1 : 0;
    const slot = this.slotAt(free);
    slot.bake.step(BAKE_BUDGET);
    if (slot.bake.done && !slot.uploaded) {
      this.finishBake(free);
    }

    const event = this.timeline.update(deltaSeconds, slot.bake.done);
    if (event === 'blend-started') {
      this.startBlend();
    } else if (event === 'arrived') {
      this.arrive();
    }

    this.uniforms.blend.value = this.timeline.progress;
    this.uniforms.time.value = this.elapsed;
    this.advanceSpins(deltaSeconds);
    this.driveFromAudio(frame, deltaSeconds);
  }

  private makeSlot(geometry: InstancedBufferGeometry, index: number, segment: Segment): Slot {
    const bake = new ShapeBake(PARTICLES);
    const form = new InstancedBufferAttribute(bake.form, 4);
    const motion = new InstancedBufferAttribute(bake.motion, 4);
    geometry.setAttribute(`form${String(index)}`, form);
    geometry.setAttribute(`motion${String(index)}`, motion);
    return {
      bake,
      form,
      motion,
      segment,
      bounds: { x: 0, y: 0, z: 0, radius: 1 },
      spin: 0,
      uploaded: false,
    };
  }

  private shapeAt(index: number): ShapeDefinition {
    const shape = this.shapes[index] ?? this.shapes[0];
    if (shape === undefined) {
      throw new Error('The shape registry is empty.');
    }
    return shape;
  }

  /** Tuple indexing that survives `noUncheckedIndexedAccess`. */
  private slotAt(index: number): Slot {
    return index === 0 ? this.slots[0] : this.slots[1];
  }

  private beginBake(index: number, segment: Segment): void {
    const slot = this.slotAt(index);
    slot.segment = segment;
    slot.spin = 0;
    slot.uploaded = false;
    slot.bake.begin(this.shapeAt(segment.shape), segment.seed, segment.variant);
  }

  private bakeNow(index: number): void {
    const segment = index === 0 ? this.timeline.current : this.timeline.incoming;
    this.beginBake(index, segment);
    this.slotAt(index).bake.step(PARTICLES);
    this.finishBake(index);
  }

  private finishBake(index: number): void {
    const slot = this.slotAt(index);
    slot.bounds = slot.bake.bounds;
    slot.uploaded = true;
    slot.form.needsUpdate = true;
    slot.motion.needsUpdate = true;
  }

  /** Copy a segment's palette, drift rate and centre into its slot's uniforms. */
  private applySegment(index: number, segment: Segment): void {
    const slot = this.slotAt(index);
    const target = index === 0 ? this.uniforms.slotA : this.uniforms.slotB;
    const shape = this.shapeAt(segment.shape);
    target.base.value = tinted(shape.palette.base, segment.hueShift);
    target.shade.value = tinted(shape.palette.shade, segment.hueShift);
    target.accent.value = tinted(shape.palette.accent, segment.hueShift * 0.4);
    target.rate.value = shape.driftRate;
    target.centre.value.set(slot.bounds.x, slot.bounds.y, slot.bounds.z);
  }

  private startBlend(): void {
    const incoming = this.timeline.incoming;
    const incomingSlot = this.flip === 0 ? 1 : 0;
    const destination = this.slotAt(incomingSlot);
    const u = this.uniforms;
    const { transition } = incoming;
    u.byRandom.value = transition.byRandom;
    u.byHeight.value = transition.byHeight;
    u.byRadius.value = transition.byRadius;
    u.byAngle.value = transition.byAngle;
    u.byLateral.value = transition.byLateral;
    u.spread.value = transition.spread;
    this.applySegment(incomingSlot, incoming);
    this.focusOn(destination.bounds);
  }

  private arrive(): void {
    this.visits += 1;
    document.title = `${this.shapeAt(this.timeline.current.shape).name} | ${this.timeline.current.camera}`;
    this.flip = this.flip === 0 ? 1 : 0;
    this.uniforms.flip.value = this.flip;
    this.focusOn(this.focus);
    const free = this.flip === 0 ? 1 : 0;
    this.beginBake(free, this.timeline.incoming);
  }

  private focusOn(bounds: Bounds): void {
    this.uniforms.focus.value.set(bounds.x, bounds.y, bounds.z);
    this.uniforms.reach.value = bounds.radius;
  }

  private advanceSpins(deltaSeconds: number): void {
    const [a, b] = this.slots;
    a.spin += a.segment.spin * deltaSeconds;
    b.spin += b.segment.spin * deltaSeconds;
    this.uniforms.slotA.spin.value = a.spin;
    this.uniforms.slotB.spin.value = b.spin;
    this.uniforms.slotA.centre.value.set(a.bounds.x, a.bounds.y, a.bounds.z);
    this.uniforms.slotB.centre.value.set(b.bounds.x, b.bounds.y, b.bounds.z);
  }

  /**
   * The audio's whole influence on the look. Every brightness-shaped dial goes
   * through a `Gentle`, whose rate limit is what keeps a busy track from turning
   * the screen into a strobe; only motion is allowed to react quickly.
   */
  private driveFromAudio(frame: AudioFrame, deltaSeconds: number): void {
    const u = this.uniforms;
    const segment = this.timeline.current;
    const swell = this.breath.update(frame.bass * 0.75 + frame.level * 0.25, deltaSeconds);
    const motion = this.flowLevel.update(frame.mid * 0.6 + frame.level * 0.4, deltaSeconds);
    const bulge = Math.sin(this.timeline.progress * Math.PI);

    u.glow.value = this.glow.update(segment.glow * (0.85 + frame.level * 0.18), deltaSeconds);
    u.size.value = this.size.update(0.062 + frame.level * 0.022 + bulge * 0.015, deltaSeconds);
    u.opacity.value = this.opacity.update(0.42 + frame.level * 0.14, deltaSeconds);
    u.shimmer.value = this.shimmer.update(0.06 + frame.treble * 0.28, deltaSeconds);
    u.flow.value = 0.14 + motion * 0.45;
    u.turbulence.value = this.timeline.incoming.transition.turbulence * (1.2 + motion * 0.55);
    // `pulse` deepens the swell, not the brightness, so it can never strobe.
    u.breath.value = swell * this.pulse;

    /*
     * The breathing pulse. The envelope rises smoothly on onsets and relaxes
     * buoyantly over half a second — giving an organic respiration wave
     * rather than an instantaneous knife-edge spike.
     */
    const rawPulse = this.breathingPulse.update(frame, deltaSeconds);
    u.beatPulse.value = Math.min(2.5, rawPulse * this.pulse);

    // Drift runs on its own clock: slow in silence, urgent when the track is.
    this.driftClock += deltaSeconds * (0.55 + motion * 1.1 + u.beatPulse.value * 0.65);
    u.driftTime.value = this.driftClock;

    const shape = this.shapeAt(segment.shape);
    this.ambient.set(shape.palette.accent);
    u.haze.value.setRGB(this.ambient.r * 0.05, this.ambient.g * 0.05, this.ambient.b * 0.07);
  }
}
